import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronRight } from "lucide-react";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import {
  approveChangeRequest,
  denyChangeRequest,
  listChangeRequests,
  mintChangeOtp,
  type ChangeRequest,
} from "@/shared/api/changes";
import {
  approveOverrideRequest,
  denyOverrideRequest,
  listOtpWallet,
  listOverrideRequests,
  mintOtpWallet,
  mintOverrideOtp,
  type ApprovalCategory,
  type OverrideRequest,
} from "@/shared/api/overrides";
import { AdminList, AdminListRow } from "@/modules/shell/AdminList";
import { AdvancedSection } from "@/modules/shell/AdvancedSection";
import { AppSheet } from "@/modules/shell/AppSheet";
import { EmptyState } from "@/modules/shell/EmptyState";
import { PageFrame } from "@/modules/shell/PageFrame";
import { PageHeader } from "@/modules/shell/PageHeader";
import { TagsAssigneesPanel } from "@/modules/tags/components/TagsAssigneesPanel";
import {
  queryErrorMessage,
  useAuthRedirectOnQueryError,
} from "@/shared/query/query-error";
import { queryKeys } from "@/shared/query/keys";
import {
  defaultApprovalsTab,
  humanizeApprovalSource,
  humanizePermissionKey,
  payloadRows,
  type ApprovalsTab,
} from "../approvals-math";

/** Phone Approvals — list first; detail in sheet — plan/27, 35. */
const APPROVALS_COMPACT_MQ = "(max-width: 767px)";

const PREGEN_PERMISSIONS = [
  "pos.void",
  "pos.discount_over_cap",
  "pos.oversell",
  "pos.return",
] as const;

type InboxItem =
  | { source: "override"; row: OverrideRequest }
  | { source: "change"; row: ChangeRequest };

function itemKey(item: InboxItem): string {
  return `${item.source}:${item.row.id}`;
}

export function ApprovalsPage() {
  const { companySlug } = useParams();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<ApprovalsTab>("destructive");
  const [tabReady, setTabReady] = useState(false);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [reviewed, setReviewed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [otpCopied, setOtpCopied] = useState(false);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [scope, setScope] = useState<"transaction" | "hour" | "today">(
    "transaction",
  );
  const [mintedOtp, setMintedOtp] = useState<{
    code: string;
    expiresAt: string;
    permissionKey: string;
  } | null>(null);
  const [walletPerm, setWalletPerm] =
    useState<(typeof PREGEN_PERMISSIONS)[number]>("pos.void");
  const [walletCount, setWalletCount] = useState(5);
  const [walletBusy, setWalletBusy] = useState(false);
  const [walletFresh, setWalletFresh] = useState<
    Array<{ code: string; permissionKey: string; expiresAt: string }>
  >([]);
  const [compact, setCompact] = useState(() =>
    typeof window !== "undefined"
      ? window.matchMedia(APPROVALS_COMPACT_MQ).matches
      : true,
  );

  useEffect(() => {
    const mq = window.matchMedia(APPROVALS_COMPACT_MQ);
    const onChange = () => setCompact(mq.matches);
    onChange();
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  const overridesQuery = useQuery({
    queryKey: queryKeys.approvals.overrides(companySlug ?? ""),
    enabled: Boolean(companySlug),
    queryFn: () => listOverrideRequests(companySlug!, "pending"),
  });
  const changesQuery = useQuery({
    queryKey: queryKeys.approvals.changes(companySlug ?? ""),
    enabled: Boolean(companySlug),
    queryFn: () => listChangeRequests(companySlug!, "pending"),
  });
  const walletQuery = useQuery({
    queryKey: queryKeys.approvals.wallet(companySlug ?? ""),
    enabled: Boolean(companySlug),
    queryFn: () => listOtpWallet(companySlug!),
    retry: false,
  });
  const errorMsg = queryErrorMessage(
    overridesQuery.error ?? changesQuery.error,
    overridesQuery.isError || changesQuery.isError,
  );
  useAuthRedirectOnQueryError(errorMsg);

  const items = useMemo<InboxItem[]>(() => {
    const overrides = overridesQuery.data?.requests ?? [];
    const changes = changesQuery.data?.requests ?? [];
    return [
      ...overrides.map((row) => ({
        source: "override" as const,
        row,
      })),
      ...changes.map((row) => ({
        source: "change" as const,
        row,
      })),
    ].sort(
      (a, b) =>
        new Date(a.row.createdAt).getTime() -
        new Date(b.row.createdAt).getTime(),
    );
  }, [overridesQuery.data, changesQuery.data]);

  const counts = useMemo(() => {
    const overrides = overridesQuery.data?.counts;
    const changes = changesQuery.data?.counts;
    return {
      pending: (overrides?.pending ?? 0) + (changes?.pending ?? 0),
      operational:
        (overrides?.operational ?? 0) + (changes?.operational ?? 0),
      destructive:
        (overrides?.destructive ?? 0) + (changes?.destructive ?? 0),
    };
  }, [overridesQuery.data, changesQuery.data]);

  const wallet = walletQuery.data?.codes ?? [];
  const loading = overridesQuery.isPending || changesQuery.isPending;
  const displayError =
    error ??
    (errorMsg && errorMsg !== "Not signed in" ? errorMsg : null);

  async function reload() {
    if (!companySlug) return;
    setError(null);
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: queryKeys.approvals.all(companySlug),
      }),
    ]);
  }

  useEffect(() => {
    if (loading || tabReady) return;
    setTab(defaultApprovalsTab(counts));
    setTabReady(true);
  }, [loading, tabReady, counts]);

  useEffect(() => {
    setSelectedKey((prev) => {
      if (prev && items.some((i) => itemKey(i) === prev)) return prev;
      return null;
    });
    setReviewed(false);
  }, [items]);

  useEffect(() => {
    setSelectedKey(null);
    setReviewed(false);
  }, [tab]);

  if (!companySlug) return null;

  const filtered = items.filter((item) => {
    if (tab === "all") return true;
    return item.row.category === tab;
  });

  const selected = selectedKey
    ? (filtered.find((i) => itemKey(i) === selectedKey) ??
      items.find((i) => itemKey(i) === selectedKey) ??
      null)
    : null;

  const selectedCategory = selected?.row.category ?? null;
  const effectiveScope =
    selectedCategory === "destructive" ? "transaction" : scope;
  const sheetOpen = compact && selected != null;

  async function decide(item: InboxItem, action: "approve" | "deny") {
    if (
      item.row.category === "destructive" &&
      action === "approve" &&
      !reviewed
    ) {
      setError("Review the request detail before approving");
      return;
    }
    const key = itemKey(item);
    setBusyKey(key);
    setError(null);
    try {
      if (item.source === "override") {
        if (action === "approve") {
          await approveOverrideRequest(companySlug!, item.row.id, {
            grantScope: effectiveScope,
          });
        } else {
          await denyOverrideRequest(companySlug!, item.row.id);
        }
      } else if (action === "approve") {
        await approveChangeRequest(companySlug!, item.row.id);
      } else {
        await denyChangeRequest(companySlug!, item.row.id);
      }
      setSelectedKey(null);
      setMintedOtp(null);
      await reload();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed");
    } finally {
      setBusyKey(null);
    }
  }

  function selectItem(item: InboxItem) {
    setSelectedKey(itemKey(item));
    setReviewed(false);
    setError(null);
    setMintedOtp(null);
    setOtpCopied(false);
  }

  async function copyMintedOtp() {
    if (!mintedOtp) return;
    try {
      await navigator.clipboard.writeText(mintedOtp.code);
      setOtpCopied(true);
      window.setTimeout(() => setOtpCopied(false), 2000);
    } catch {
      setError("Could not copy OTP");
    }
  }

  async function onMintOtp(item: InboxItem) {
    if (item.row.category === "destructive" && !reviewed) {
      setError("Review the request detail before minting an OTP");
      return;
    }
    const key = itemKey(item);
    setBusyKey(key);
    setError(null);
    setMintedOtp(null);
    setOtpCopied(false);
    try {
      const { otp } =
        item.source === "override"
          ? await mintOverrideOtp(companySlug!, item.row.id, {
              grantScope: effectiveScope,
            })
          : await mintChangeOtp(companySlug!, item.row.id);
      setMintedOtp({
        code: otp.code,
        expiresAt: otp.expiresAt,
        permissionKey: otp.permissionKey,
      });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Mint failed");
    } finally {
      setBusyKey(null);
    }
  }

  function tabLabel(t: ApprovalsTab, n: number) {
    const title =
      t === "destructive"
        ? "Destructive"
        : t === "operational"
          ? "Operational"
          : "All";
    return n > 0 ? `${title} (${n})` : title;
  }

  function renderDetail(item: InboxItem, opts: { inSheet: boolean }) {
    const busy = busyKey === itemKey(item);
    return (
      <div className="space-y-4">
        <div>
          <div className="text-xs text-[var(--color-muted)]">
            {humanizeApprovalSource(item.source)} ·{" "}
            {item.row.category as ApprovalCategory}
          </div>
          <div className="text-base font-semibold">
            {humanizePermissionKey(item.row.permissionKey)}
          </div>
          <p className="mt-0.5 font-mono text-[11px] text-[var(--color-muted)]">
            {item.row.permissionKey}
          </p>
          <p className="mt-1 text-sm text-[var(--color-muted)]">
            {item.row.summary}
          </p>
        </div>

        <dl className="m-0 space-y-1 text-xs">
          <div className="flex justify-between gap-2">
            <dt className="text-[var(--color-muted)]">Requested</dt>
            <dd className="m-0 font-mono">
              {new Date(item.row.createdAt).toLocaleString("en-PH", {
                timeZone: "Asia/Manila",
              })}
            </dd>
          </div>
        </dl>

        {item.source === "change" && item.row.diffs.length > 0 && (
          <div>
            <div className="text-xs font-medium">Before → after</div>
            <ul className="mt-1 m-0 list-none space-y-2 border-t border-[var(--color-border)] pt-2 p-0">
              {item.row.diffs.map((d) => (
                <li key={d.field} className="text-xs">
                  <div className="font-medium">{d.field}</div>
                  <div className="mt-0.5 flex justify-between gap-2 font-mono">
                    <span className="text-[var(--color-muted)]">
                      {d.before ?? "—"}
                    </span>
                    <span>→</span>
                    <span className="text-right">{d.after ?? "—"}</span>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}

        {item.source === "override" &&
          payloadRows(item.row.payload).length > 0 && (
            <div>
              <div className="text-xs font-medium">Details</div>
              <ul className="mt-1 m-0 list-none space-y-1 border-t border-[var(--color-border)] pt-2 p-0">
                {payloadRows(item.row.payload).map((row) => (
                  <li
                    key={row.key}
                    className="flex justify-between gap-3 font-mono text-xs"
                  >
                    <span className="text-[var(--color-muted)]">{row.key}</span>
                    <span className="text-right">{row.value}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

        {item.row.category !== "destructive" && opts.inSheet && (
          <div>
            <p className="m-0 text-xs text-[var(--color-muted)]">Grant scope</p>
            <div className="mt-1.5 flex flex-wrap gap-1.5" role="group">
              {(
                [
                  ["transaction", "This txn"],
                  ["hour", "1 hour"],
                  ["today", "Today"],
                ] as const
              ).map(([value, label]) => (
                <Button
                  key={value}
                  type="button"
                  size="sm"
                  variant={scope === value ? "default" : "outline"}
                  className="min-h-10"
                  onClick={() => setScope(value)}
                >
                  {label}
                </Button>
              ))}
            </div>
          </div>
        )}

        {item.row.category === "destructive" && (
          <label className="flex min-h-11 items-start gap-3 text-sm">
            <input
              type="checkbox"
              className="mt-1 size-4"
              checked={reviewed}
              onChange={(e) => setReviewed(e.target.checked)}
            />
            <span>I've reviewed this request</span>
          </label>
        )}

        {!opts.inSheet && (
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              size="sm"
              pending={busy}
              pendingLabel={
                item.source === "change" ? "Applying…" : "Approving…"
              }
              disabled={item.row.category === "destructive" && !reviewed}
              onClick={() => void decide(item, "approve")}
            >
              {item.source === "change" ? "Approve & apply" : "Approve"}
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              pending={busy}
              pendingLabel="Denying…"
              onClick={() => void decide(item, "deny")}
            >
              Deny
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              pending={busy}
              pendingLabel="Minting…"
              disabled={item.row.category === "destructive" && !reviewed}
              onClick={() => void onMintOtp(item)}
            >
              Emergency OTP
            </Button>
          </div>
        )}

        {opts.inSheet && (
          <Button
            type="button"
            variant="outline"
            className="min-h-11 w-full"
            pending={busy}
            pendingLabel="Minting…"
            disabled={item.row.category === "destructive" && !reviewed}
            onClick={() => void onMintOtp(item)}
          >
            Emergency OTP
          </Button>
        )}

        {mintedOtp && (
          <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)]/80 px-4 py-3 shadow-[0_1px_0_color-mix(in_oklab,var(--color-foreground)_4%,transparent)]">
            <div className="text-xs text-[var(--color-muted)]">
              Read aloud once · {mintedOtp.permissionKey}
            </div>
            <div className="mt-1 font-mono text-2xl tracking-[0.35em]">
              {mintedOtp.code}
            </div>
            <div className="mt-1 flex flex-wrap items-center justify-between gap-2">
              <span className="text-xs text-[var(--color-muted)]">
                Expires{" "}
                {new Date(mintedOtp.expiresAt).toLocaleTimeString("en-PH", {
                  timeZone: "Asia/Manila",
                })}{" "}
                (Asia/Manila)
              </span>
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="min-h-10"
                onClick={() => void copyMintedOtp()}
              >
                {otpCopied ? "Copied" : "Copy"}
              </Button>
            </div>
          </div>
        )}

        <TagsAssigneesPanel
          companySlug={companySlug!}
          docType={
            item.source === "override" ? "override_request" : "change_request"
          }
          docId={item.row.id}
          linkPath={`/c/${companySlug}/approvals`}
        />
      </div>
    );
  }

  function renderWallet() {
    return (
      <>
        <p className="mt-1 text-xs text-[var(--color-muted)]">
          Pre-mint permission-scoped codes for when you cannot open Approvals.
          Each code works only for that soft-limit type. Plaintext shown once.
        </p>

        <div className="mt-4 flex flex-wrap items-end gap-3">
          <div>
            <Label htmlFor="wallet-perm">Permission</Label>
            <Select
              id="wallet-perm"
              className="mt-1 min-w-[12rem]"
              value={walletPerm}
              onChange={(e) =>
                setWalletPerm(
                  e.target.value as (typeof PREGEN_PERMISSIONS)[number],
                )
              }
            >
              {PREGEN_PERMISSIONS.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </Select>
          </div>
          <div>
            <Label htmlFor="wallet-count">Count</Label>
            <Input
              id="wallet-count"
              type="number"
              min={1}
              max={10}
              className="mt-1 w-20"
              value={walletCount}
              onChange={(e) =>
                setWalletCount(
                  Math.min(10, Math.max(1, Number(e.target.value) || 1)),
                )
              }
            />
          </div>
          <Button
            type="button"
            size="sm"
            className="min-h-11"
            pending={walletBusy}
            pendingLabel="Minting…"
            onClick={() => {
              setWalletBusy(true);
              setError(null);
              setWalletFresh([]);
              void mintOtpWallet(companySlug!, {
                permissionKey: walletPerm,
                count: walletCount,
              })
                .then((d) => {
                  setWalletFresh(d.codes);
                  void queryClient.invalidateQueries({
                    queryKey: queryKeys.approvals.wallet(companySlug!),
                  });
                })
                .catch((err: unknown) =>
                  setError(err instanceof Error ? err.message : "Mint failed"),
                )
                .finally(() => setWalletBusy(false));
            }}
          >
            Mint batch
          </Button>
        </div>

        {walletFresh.length > 0 && (
          <div className="mt-4 rounded-xl border border-[var(--color-border)] bg-[var(--color-background)]/80 px-4 py-3 shadow-[0_1px_0_color-mix(in_oklab,var(--color-foreground)_4%,transparent)]">
            <div className="text-xs text-[var(--color-muted)]">
              Copy now — codes will not be shown again
            </div>
            <ul className="mt-2 m-0 list-none space-y-1 p-0 font-mono text-lg tracking-[0.2em]">
              {walletFresh.map((c) => (
                <li key={c.code}>
                  {c.code}{" "}
                  <span className="text-xs tracking-normal text-[var(--color-muted)]">
                    {c.permissionKey}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {wallet.length > 0 ? (
          <ul className="mt-4 m-0 list-none space-y-1 p-0 text-xs text-[var(--color-muted)]">
            {wallet.map((c) => (
              <li key={c.id} className="flex justify-between gap-3">
                <span className="font-mono text-[var(--color-foreground)]">
                  {c.permissionKey}
                </span>
                <span>
                  expires{" "}
                  {new Date(c.expiresAt).toLocaleString("en-PH", {
                    timeZone: "Asia/Manila",
                  })}
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="mt-4 text-xs text-[var(--color-muted)]">
            No unused wallet codes yet.
          </p>
        )}
      </>
    );
  }

  return (
    <PageFrame>
      <PageHeader
        title="Approvals"
        description={
          compact
            ? "One at a time — tap a request to review."
            : "Overrides and change requests — one at a time, no approve-all."
        }
      />

      <p className="mt-4 flex flex-wrap gap-x-1 gap-y-1 text-sm sm:gap-x-4">
        {(
          [
            ["destructive", counts.destructive],
            ["operational", counts.operational],
            ["all", counts.pending],
          ] as const
        ).map(([t, n]) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            className={cn(
              "min-h-11 px-2 pb-0.5 sm:min-h-0 sm:px-0",
              tab === t
                ? "border-b-2 border-[var(--color-foreground)] font-medium text-[var(--color-foreground)]"
                : "border-b-2 border-transparent text-[var(--color-muted)] hover:text-[var(--color-foreground)]",
            )}
          >
            {tabLabel(t, n)}
          </button>
        ))}
      </p>

      {tab !== "destructive" && !compact && (
        <div className="mt-6">
          <Label htmlFor="approval-grant-scope">
            Grant scope when approving overrides (operational)
          </Label>
          <Select
            id="approval-grant-scope"
            className="mt-1"
            value={scope}
            onChange={(e) =>
              setScope(e.target.value as "transaction" | "hour" | "today")
            }
          >
            <option value="transaction">This transaction only</option>
            <option value="hour">Similar for 1 hour</option>
            <option value="today">Similar for today</option>
          </Select>
        </div>
      )}
      {tab === "destructive" && (
        <p className="mt-4 text-xs text-[var(--color-muted)] sm:mt-6">
          Destructive: overrides stay this-transaction; change requests apply
          the draft on approve.
        </p>
      )}

      {displayError && (
        <Alert variant="destructive" className="mt-4">
          {displayError}
        </Alert>
      )}
      {loading && (
        <Alert variant="muted" className="mt-8">
          Loading approvals…
        </Alert>
      )}

      {!loading && (
        <div
          className={cn(
            "mt-6",
            !compact && "grid gap-6 md:grid-cols-[1fr_1.1fr]",
          )}
        >
          {filtered.length === 0 && !displayError ? (
            <EmptyState
              title={counts.pending === 0 ? "All clear" : "Nothing in this tab"}
              description={
                counts.pending === 0
                  ? "Override and change requests land here one at a time when staff hit a soft limit."
                  : `No pending ${tab === "all" ? "requests" : `${tab} requests`} here.`
              }
            />
          ) : (
            <AdminList>
              {filtered.map((item) => (
                <AdminListRow key={itemKey(item)} className="p-0">
                  <button
                    type="button"
                    onClick={() => selectItem(item)}
                    className={cn(
                      "flex w-full items-center gap-2 px-4 py-3.5 text-left transition-colors",
                      compact ? "min-h-[4.5rem]" : "",
                      selectedKey === itemKey(item) && !compact
                        ? "bg-[var(--color-background)]"
                        : "hover:bg-[var(--color-background)]/70",
                    )}
                  >
                    <span className="flex min-w-0 flex-1 flex-col items-start gap-0.5">
                      <span className="text-xs text-[var(--color-muted)]">
                        {humanizeApprovalSource(item.source)} ·{" "}
                        {item.row.category}
                      </span>
                      <span className="text-sm font-medium">
                        {humanizePermissionKey(item.row.permissionKey)}
                      </span>
                      <span className="line-clamp-2 text-xs text-[var(--color-muted)]">
                        {item.row.summary}
                      </span>
                    </span>
                    {compact ? (
                      <ChevronRight
                        className="size-5 shrink-0 text-[var(--color-muted)]"
                        aria-hidden
                      />
                    ) : null}
                  </button>
                </AdminListRow>
              ))}
            </AdminList>
          )}

          {!compact && (
            <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-card)]/80 p-4 shadow-[0_1px_0_color-mix(in_oklab,var(--color-foreground)_4%,transparent)]">
              {!selected ? (
                <p className="m-0 text-sm leading-relaxed text-[var(--color-muted)]">
                  {filtered.length === 0
                    ? "Inbox is quiet — pick another tab if counts show work elsewhere."
                    : `Select a request to review${
                        tab === "destructive" ? " before approving" : ""
                      }.`}
                </p>
              ) : (
                renderDetail(selected, { inSheet: false })
              )}
            </div>
          )}
        </div>
      )}

      {selected && (
        <AppSheet
          open={sheetOpen}
          onOpenChange={(open) => {
            if (!open) {
              setSelectedKey(null);
              setMintedOtp(null);
              setError(null);
            }
          }}
          title="Review"
          description="Review and approve or deny this request"
          trailing={
            <Button
              type="button"
              className="min-h-11"
              pending={busyKey === itemKey(selected)}
              pendingLabel={
                selected.source === "change" ? "Applying…" : "Approving…"
              }
              disabled={
                selected.row.category === "destructive" && !reviewed
              }
              onClick={() => void decide(selected, "approve")}
            >
              {selected.source === "change" ? "Apply" : "Approve"}
            </Button>
          }
          footer={
            <Button
              type="button"
              variant="outline"
              className="min-h-12 w-full text-[var(--color-destructive)]"
              pending={busyKey === itemKey(selected)}
              pendingLabel="Denying…"
              onClick={() => void decide(selected, "deny")}
            >
              Deny
            </Button>
          }
        >
          {displayError && (
            <Alert variant="destructive" className="mb-3">
              {displayError}
            </Alert>
          )}
          {renderDetail(selected, { inSheet: true })}
        </AppSheet>
      )}

      {compact ? (
        <AdvancedSection
          title="Emergency OTP wallet"
          subtitle="pre-mint codes"
          className="mt-8"
        >
          {renderWallet()}
        </AdvancedSection>
      ) : (
        <section className="mt-12 border-t border-[var(--color-border)] pt-6">
          <h2 className="m-0 text-sm font-semibold text-[var(--color-muted)]">
            Emergency OTP wallet
          </h2>
          {renderWallet()}
        </section>
      )}

      <p className="mt-8 text-sm">
        <Link to={`/c/${companySlug}`} className="no-underline">
          ← Company home
        </Link>
      </p>
    </PageFrame>
  );
}
